import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, CardContent, Typography, TextField, Button, Stack, Alert, CircularProgress, Grid } from '@mui/material';
import { settingsApi } from '../services/api';
import { useShell } from '../components/Layout';
import PageHeader from '../components/common/PageHeader';
import EmailConfirmCard from '../components/EmailConfirmCard';
import { apiError, planLabel } from '../utils/format';
import { tokens } from '../theme';

const ProfileTab = () => {
  const { profile, reloadShell } = useShell();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => setName(profile?.full_name || ''), [profile]);
  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setMsg(null);
    try {
      await settingsApi.updateMe(name);
      await reloadShell();
      setMsg({ type: 'success', text: 'Имя сохранено' });
    } catch (err) {
      setMsg({ type: 'error', text: apiError(err) });
    } finally {
      setSaving(false);
    }
  };
  const trimmed = name.trim();
  const nameError = trimmed.length === 0 ? 'Укажите имя' : trimmed.length > 100 ? 'Не длиннее 100 символов' : '';
  return (
    <Card component="form" onSubmit={submit} noValidate sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h3" sx={{ mb: 2 }}>Личные данные</Typography>
        <Stack spacing={2} >
          <TextField label="Имя" value={name} onChange={(e) => setName(e.target.value)} required
            error={Boolean(msg?.type === 'error' || (name && nameError))} helperText={name ? nameError || 'Используется в приветствии на главной' : 'Используется в приветствии на главной'} />
          <TextField label="Email" value={profile?.email || ''} disabled helperText="Email меняет администратор сервиса" />
          {msg && <Alert severity={msg.type}>{msg.text}</Alert>}
          <Box>
            <Button type="submit" variant="contained" disabled={saving || Boolean(nameError) || trimmed === (profile?.full_name || '')}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
              Сохранить
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const PasswordTab = () => {
  const [form, setForm] = useState({ current: '', next: '', repeat: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const errors = {
    next: form.next && form.next.length < 8 ? 'Не менее 8 символов' : '',
    repeat: form.repeat && form.repeat !== form.next ? 'Пароли не совпадают' : '',
  };
  const valid = form.current && form.next.length >= 8 && form.next === form.repeat;
  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setMsg(null);
    try {
      await settingsApi.changePassword(form.current, form.next);
      setForm({ current: '', next: '', repeat: '' });
      setMsg({ type: 'success', text: 'Пароль изменён' });
    } catch (err) {
      setMsg({ type: 'error', text: apiError(err) });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card component="form" onSubmit={submit} noValidate sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h3" sx={{ mb: 2 }}>Смена пароля</Typography>
        <Stack spacing={2} >
          <TextField type="password" label="Текущий пароль" autoComplete="current-password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} required />
          <TextField type="password" label="Новый пароль" autoComplete="new-password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })}
            error={Boolean(errors.next)} helperText={errors.next || 'Не менее 8 символов'} required />
          <TextField type="password" label="Повторите новый пароль" autoComplete="new-password" value={form.repeat} onChange={(e) => setForm({ ...form, repeat: e.target.value })}
            error={Boolean(errors.repeat)} helperText={errors.repeat} required />
          {msg && <Alert severity={msg.type}>{msg.text}</Alert>}
          <Box>
            <Button type="submit" variant="contained" disabled={!valid || saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
              Изменить пароль
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Joining the partner program is an application; once it is sent the section «Партнёрам» appears in the menu.
const PARTNER_TEXT = {
  none: { text: 'Приводите клиентов по своей ссылке и получайте вознаграждение с их первой оплаты. Подайте заявку: после одобрения администратором ссылка заработает.', button: 'Подать заявку' },
  pending: { text: 'Ваша заявка на рассмотрении. Ссылка заработает после одобрения.', button: 'Открыть заявку' },
  active: { text: 'Вы участвуете в партнёрской программе.', button: 'Открыть раздел' },
  blocked: { text: 'Участие в партнёрской программе приостановлено.', button: 'Открыть раздел' },
  rejected: { text: 'Заявка отклонена. Вы можете исправить данные и подать её снова.', button: 'Открыть заявку' },
};

// The application is filed from a confirmed email only: until then the button does not work.
const PartnerCard = ({ status, confirmed }) => {
  const t = PARTNER_TEXT[status] || PARTNER_TEXT.none;
  const blocked = !confirmed && status === 'none';
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="h3" sx={{ mb: 0.5 }}>Партнёрская программа</Typography>
          <Typography color="text.secondary">{t.text}</Typography>
          {blocked && <Typography variant="body2" sx={{ mt: 0.75, color: tokens.warning }}>Подать заявку можно после подтверждения почты.</Typography>}
        </Box>
        <Button component={RouterLink} to="/partner" variant="contained" disabled={blocked}>{t.button}</Button>
      </CardContent>
    </Card>
  );
};

const SettingsPage = () => {
  const { profile, partner, reloadShell } = useShell();
  const confirmed = profile?.restriction !== 'email_unverified';
  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <PageHeader overline="Настройки" title="Профиль и безопасность" subtitle="Налоги и НДС задаются для каждого маркетплейса отдельно — в разделе «Себестоимость и налоги» площадки." />
      <Card sx={{ mb: 2.5, background: tokens.heroGradient }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '16px', bgcolor: tokens.profile, color: '#7FE3DC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.6rem' }}>
            {(profile?.full_name || profile?.email || '?').slice(0, 1).toUpperCase()}
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h3">{profile?.full_name || profile?.email || '—'}</Typography>
            <Typography color="text.secondary">{profile?.email}</Typography>
          </Box>
          <Box sx={{ textAlign: { sm: 'right' } }}>
            {profile?.team?.member ? (
              <>
                <Typography variant="body2" color="text.secondary">Роль</Typography>
                <Typography sx={{ fontWeight: 700, color: tokens.primaryDark }}>{profile.team.role_label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  сотрудник {profile.team.owner?.full_name || profile.team.owner?.email || 'владельца аккаунта'}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">Тариф</Typography>
                <Typography sx={{ fontWeight: 700, color: tokens.primaryDark }}>{planLabel(profile) || '—'}</Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
      {!confirmed && (
        <Box sx={{ mb: 2.5 }}>
          <EmailConfirmCard email={profile?.email} onVerified={reloadShell} />
        </Box>
      )}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}><ProfileTab /></Grid>
        <Grid item xs={12} md={6}><PasswordTab /></Grid>
        {!profile?.team?.member && <Grid item xs={12}><PartnerCard status={partner} confirmed={confirmed} /></Grid>}
      </Grid>
    </Box>
  );
};

export default SettingsPage;
